// Dates de la roadmap en jours entiers (jours depuis 1970, en UTC) : pas de fuseau ni d'heure d'été dans les calculs.

const DAY_MS = 86400000;

export const toDay = (iso) => {
  if (!iso) return null;
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return y && m && d ? Math.round(Date.UTC(y, m - 1, d) / DAY_MS) : null;
};

export const toIso = (day) => new Date(day * DAY_MS).toISOString().slice(0, 10);

export const todayDay = (now = new Date()) => Math.round(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / DAY_MS);

export const weekday = (day) => new Date(day * DAY_MS).getUTCDay(); // 0 = dimanche

export const nextMonday = (day) => day + ((8 - weekday(day)) % 7);

const formatter = (options) => new Intl.DateTimeFormat('fr-FR', { ...options, timeZone: 'UTC' });
const SHORT = formatter({ day: 'numeric', month: 'short' });
const MONTH = formatter({ month: 'short' });
const MONTH_YEAR = formatter({ month: 'short', year: 'numeric' });

export const dayLabel = (day) => SHORT.format(new Date(day * DAY_MS));

export const rangeLabel = (start, end) => `${dayLabel(start)} → ${dayLabel(end)}`;

/** Premier jour de chaque mois couvert par [from, to], avec son libellé (l'année en janvier et au début). */
export const monthTicks = (from, to) => {
  const ticks = [];
  const first = new Date(from * DAY_MS);
  let cursor = Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), 1);
  while (cursor / DAY_MS <= to) {
    const date = new Date(cursor);
    const day = Math.round(cursor / DAY_MS);
    ticks.push({ day, label: (date.getUTCMonth() === 0 || !ticks.length ? MONTH_YEAR : MONTH).format(date) });
    cursor = Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1);
  }
  return ticks;
};

/** Où en est une période par rapport à aujourd'hui. */
export const periodState = (start, end, today) => {
  if (start == null || end == null) return 'undated';
  if (end < today) return 'done';
  if (start > today) return 'upcoming';
  return 'current';
};

export const elapsed = (start, end, today) => {
  if (start == null || end == null || end <= start) return 0;
  return Math.min(1, Math.max(0, (today - start) / (end - start)));
};

// --- Aimantation sur les créneaux (« Slot 26.10 ») : du premier lundi au dernier samedi du créneau, comme l'ancienne
// frise. En glissant une barre près d'un créneau, ses bords s'y collent.

export const slotTargets = (slots = []) => slots.map((slot) => {
  const start = toDay(slot.start_date);
  const end = toDay(slot.end_date || slot.start_date);
  if (start == null || end == null) return null;
  const monday = nextMonday(start);
  let saturday = end;
  while (weekday(saturday) !== 6 && saturday > monday) saturday -= 1;
  return monday <= saturday && monday <= end ? { id: slot.id, name: slot.name, start: monday, end: saturday } : null;
}).filter(Boolean);

export const snapThreshold = (pxPerDay) => (pxPerDay > 0 ? Math.max(2, Math.min(7, Math.round(18 / pxPerDay))) : 0);

const nearest = (targets, day, edge, threshold) => targets.reduce((best, target) => {
  const distance = Math.abs(day - target[edge]);
  return distance <= threshold && (!best || distance < best.distance) ? { target, distance } : best;
}, null);

/** Bornes après glissement (mode : move, start, end), collées au créneau le plus proche s'il est à portée. */
export const snapRange = (start, end, mode, targets, threshold) => {
  if (!targets.length || threshold <= 0) return { start, end };
  if (mode === 'start') {
    const hit = nearest(targets, start, 'start', threshold);
    return { start: hit ? hit.target.start : start, end };
  }
  if (mode === 'end') {
    const hit = nearest(targets, end, 'end', threshold);
    return { start, end: hit ? hit.target.end : end };
  }
  const duration = end - start;
  const startHit = nearest(targets, start, 'start', threshold);
  const endHit = nearest(targets, end, 'end', threshold);
  if (startHit && (endHit?.target.id === startHit.target.id || Math.abs(end - startHit.target.end) <= threshold)) {
    return { start: startHit.target.start, end: startHit.target.end };
  }
  if (startHit) return { start: startHit.target.start, end: startHit.target.start + duration };
  if (endHit) return { start: endHit.target.end - duration, end: endHit.target.end };
  return { start, end };
};

/** Déplace ou étire une période de `delta` jours (jamais de fin avant le début). */
export const shiftRange = (start, end, mode, delta) => {
  if (mode === 'start') return { start: Math.min(start + delta, end), end };
  if (mode === 'end') return { start, end: Math.max(end + delta, start) };
  return { start: start + delta, end: end + delta };
};
